import React, { useEffect, useState } from "react";
import { Table, Spinner, Alert } from "react-bootstrap";
import { useAuth } from "../context/AuthContext";
import API from "../api";

const getOpponent = (teamId, weekNumber, matchups) => {
  const match = matchups.find(
    (m) =>
      m.week_number === weekNumber &&
      (m.team1 === teamId || m.team2 === teamId)
  );
  if (!match) return "";
  if (match.is_bye) return "BYE";
  return match.team1 === teamId ? match.team2 : match.team1;
};

const formatDate = (isoDateStr) => {
  const date = new Date(isoDateStr + "T00:00:00");
  return date.toLocaleDateString("en-US", {
    month: "numeric",
    day: "2-digit",
  });
};

const isSpecialWeek = (week) =>
  week.type?.toLowerCase() === "fun round" ||
  week.type?.toLowerCase() === "position round";

export default function ScheduleGrid() {
  const { user } = useAuth();
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchSchedule = async () => {
      if (!user?.leagueid) {
        setError("No league assigned.");
        setLoading(false);
        return;
      }

      try {
        const res = await API.get(
          `/league/schedule/${user.leagueid}/schedule-grid`
        );
        setSchedule(res.data);
      } catch (err) {
        setError("Failed to load schedule");
        console.error("Schedule load error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchSchedule();
  }, [user?.leagueid]);

  if (loading) return <Spinner animation="border" className="m-5" />;
  if (error) return <Alert variant="danger">{error}</Alert>;
  if (!schedule) return null;

  const { teams, weeks, matchups, teamDetails } = schedule;

  function formatPhoneNumber(phone) {
    if (!phone) return '';
    const cleaned = ('' + phone).replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    return phone;
  }

  const midpoint = Math.ceil(teamDetails.length / 2);
  const leftColumnTeams = teamDetails.slice(0, midpoint);
  const rightColumnTeams = teamDetails.slice(midpoint);

  return (
    <div id="print-schedule" style={{ fontSize: "10px", padding: "0.5rem" }}>
      <div className="text-end mb-2 no-print">
        <button
          className="btn btn-sm btn-outline-primary"
          onClick={() => window.print()}
        >
          Print Schedule
        </button>
      </div>

      <style>
        {`@media print {
            .no-print { display: none !important; }
            @page { size: portrait; margin: 0.4in; }
            table, th, td {
              border-width: 1px !important;
            }
        }`}
      </style>

      <h4 className="text-center mb-3">
        {new Date().getFullYear()} {user?.league?.day} {user?.league?.description}
      </h4>

      <Table
        bordered
        responsive
        className="text-center"
        style={{
          fontSize: "10px",
          margin: "0 auto 1rem auto",
          borderCollapse: "collapse",
          width: "auto",
        }}
      >
        <thead>
          <tr>
            <th style={{ padding: "0.2rem 0.4rem" }}>Date</th>
            {teams.map((teamId) => (
              <th key={teamId} style={{ padding: "0.2rem 0.4rem" }}>{teamId}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((w) => {
            if (isSpecialWeek(w)) {
              return (
                <tr key={w.week_number} style={{ backgroundColor: "#e7f1ff" }}>
                  <td style={{ padding: "0.2rem 0.4rem" }}>{formatDate(w.date)}</td>
                  <td colSpan={teams.length} style={{ padding: "0.2rem 0.4rem" }}>
                    {w.type?.toLowerCase() === "fun round" && "🎉 Fun Round"}
                    {w.type?.toLowerCase() === "position round" && "Position Round"}
                  </td>
                </tr>
              );
            }

            return (
              <tr key={w.week_number}>
                <td style={{ padding: "0.2rem 0.4rem" }}>{formatDate(w.date)}</td>
                {teams.map((teamId) => (
                  <td key={teamId} style={{ padding: "0.2rem 0.4rem" }}>
                    {getOpponent(teamId, w.week_number, matchups)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </Table>

      {teamDetails && (
        <Table
          bordered
          responsive
          className="text-center"
          style={{
            fontSize: "10px",
            margin: "0 auto 0 auto",
            borderCollapse: "collapse",
            width: "auto",
          }}
        >
          <tbody>
            {leftColumnTeams.map((leftTeam, i) => {
              const rightTeam = rightColumnTeams[i];

              return (
                <React.Fragment key={i}>
                  <tr>
                    <td rowSpan={2} style={{ padding: "0.2rem 0.4rem", verticalAlign: "middle" }}>
                      {leftTeam?.id || ""}
                    </td>
                    <td style={{ padding: "0.2rem 0.4rem" }}>
                      {leftTeam?.player1
                        ? `${leftTeam.player1.firstname} ${leftTeam.player1.lastname}`
                        : ""}
                    </td>
                    <td style={{ padding: "0.2rem 0.4rem" }}>{formatPhoneNumber(leftTeam?.player1?.phone)}</td>
                    <td
                      rowSpan={2}
                      style={{
                        backgroundColor: "#dee2e6",
                        border: "none",
                        padding: 0,
                        minWidth: "5px"
                      }}
                    >
                      &nbsp;
                    </td>
                    <td rowSpan={2} style={{ padding: "0.2rem 0.4rem", verticalAlign: "middle" }}>
                      {rightTeam?.id || ""}
                    </td>
                    <td style={{ padding: "0.2rem 0.4rem" }}>
                      {rightTeam?.player1
                        ? `${rightTeam.player1.firstname} ${rightTeam.player1.lastname}`
                        : ""}
                    </td>
                    <td style={{ padding: "0.2rem 0.4rem" }}>{formatPhoneNumber(rightTeam?.player1?.phone)}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: "0.2rem 0.4rem" }}>
                      {leftTeam?.player2
                        ? `${leftTeam.player2.firstname} ${leftTeam.player2.lastname}`
                        : ""}
                    </td>
                    <td style={{ padding: "0.2rem 0.4rem" }}>{formatPhoneNumber(leftTeam?.player2?.phone)}</td>
                    <td style={{ padding: "0.2rem 0.4rem" }}>
                      {rightTeam?.player2
                        ? `${rightTeam.player2.firstname} ${rightTeam.player2.lastname}`
                        : ""}
                    </td>
                    <td style={{ padding: "0.2rem 0.4rem" }}>{formatPhoneNumber(rightTeam?.player2?.phone)}</td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </Table>
      )}
    </div>
  );
}

